/**
 * usePricingCalculatorUrlParams
 *
 * Custom hook that synchronizes pricing calculator form values with URL parameters.
 * This enables users to share quote links with pre-filled form values.
 *
 * Features:
 * - Reads initial values from URL parameters on mount
 * - Updates URL parameters when form values change (debounced)
 * - Provides a function to generate shareable URLs
 * - Uses compressed parameter names to keep URLs shorter
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorPage.tsx - Page component
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Type definitions
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PricingCalculatorFormValues, PlanType, TenantType } from '../types/pricingCalculator';
import { PRICING_CALCULATOR_PATH } from '@constants';

/**
 * Mapping of form field names to short URL parameter names
 * Using short names to keep URLs manageable
 */
const PARAM_KEYS = {
  // Base Information
  companyName: 'cn',
  subscriptionDuration: 'sd',
  isRenewal: 'rn',
  isItCustomer: 'it',

  // Core Product
  planType: 'pt',
  numberOfMembers: 'nm',
  pricePerMember: 'pm',
  discountOnMemberPrice: 'dm',
  numberOfInternalContractors: 'ic',
  numberOfGuests: 'ng',
  numberOfExternalContractors: 'ec',
  platformFee: 'pf',
  discountOnPlatformFee: 'dp',
  numberOfOnboardingGroups: 'og',
  onboardingFee: 'of',
  discountOnOnboardingFee: 'do',
  additionalEnterpriseSupportHours: 'eh',

  // Server Upgrades
  directDatabaseIntegration: 'dd',
  discountOnDirectDatabase: 'ddd',
  isolatedDatabase: 'id',
  discountOnIsolatedDatabase: 'did',
  isolatedDatabaseMigration: 'idm',
  tenantType: 'tt',
  discountOnTenant: 'dt',
  isolatedAi: 'ia',
  securityPackageSso: 'sso',
  discountOnSecurityPackageSso: 'dsso',

  // Gov Cloud
  govCloud: 'gc',
  govCloudFee: 'gcf',
  discountOnGovCloudFee: 'dgc',

  // Add-ons
  aiForecasting: 'af',
  discountOnAiForecasting: 'daf',
  nonPeopleResources: 'npr',
  discountOnNonPeopleResources: 'dnpr',
  profileBuilding: 'pb',
  discountOnProfileBuilding: 'dpb',
  skillsTagging: 'skt',
  discountOnSkillsTagging: 'dskt',
  solutionsSupport: 'ss',
  solutionsSupportHours: 'ssh',
  enableWhiteLabel: 'wl',

  // Integrations
  selectedIntegrations: 'int',
  bakIntegrationFetchTimes: 'bft',
  enable4xDataTransfer: '4x',
  integrationMigration: 'im',
  discountOnIntegrationSetup: 'dis',
  discountOnIntegrationRecurring: 'dir',
} as const;

// Reverse mapping for parsing
const PARAM_TO_FIELD = Object.fromEntries(
  Object.entries(PARAM_KEYS).map(([field, param]) => [param, field]),
) as Record<string, keyof PricingCalculatorFormValues>;

// Helper to parse boolean values
const parseBool = (value: string | null): boolean | undefined => {
  if (value === null) return undefined;
  return value === '1' || value === 'true';
};

// Helper to parse number values
const parseNum = (value: string | null): number | undefined => {
  if (value === null) return undefined;
  const num = parseFloat(value);
  return isNaN(num) ? undefined : num;
};

const BOOLEAN_PARAM_FIELDS = new Set<keyof PricingCalculatorFormValues>([
  'isRenewal',
  'isItCustomer',
  'directDatabaseIntegration',
  'isolatedDatabase',
  'isolatedDatabaseMigration',
  'isolatedAi',
  'securityPackageSso',
  'govCloud',
  'aiForecasting',
  'nonPeopleResources',
  'profileBuilding',
  'skillsTagging',
  'solutionsSupport',
  'enableWhiteLabel',
  'enable4xDataTransfer',
  'integrationMigration',
]);

const NUMBER_PARAM_FIELDS = new Set<keyof PricingCalculatorFormValues>([
  'subscriptionDuration',
  'numberOfMembers',
  'pricePerMember',
  'discountOnMemberPrice',
  'numberOfInternalContractors',
  'numberOfGuests',
  'numberOfExternalContractors',
  'platformFee',
  'discountOnPlatformFee',
  'numberOfOnboardingGroups',
  'onboardingFee',
  'discountOnOnboardingFee',
  'additionalEnterpriseSupportHours',
  'discountOnDirectDatabase',
  'discountOnIsolatedDatabase',
  'discountOnTenant',
  'discountOnSecurityPackageSso',
  'govCloudFee',
  'discountOnGovCloudFee',
  'discountOnAiForecasting',
  'discountOnNonPeopleResources',
  'discountOnProfileBuilding',
  'discountOnSkillsTagging',
  'solutionsSupportHours',
  'bakIntegrationFetchTimes',
  'discountOnIntegrationSetup',
  'discountOnIntegrationRecurring',
]);

/**
 * Apply legacy `?st=` / `?dst=` params, superseded by `?tt=` / `?dt=` when those are present.
 */
const applyLegacyParams = (
  searchParams: URLSearchParams,
  values: Partial<PricingCalculatorFormValues>,
): void => {
  // Backward compatibility: ?st=1 (legacy "single tenant environment" boolean)
  // → tenantType=SINGLE. Newer ?tt= takes precedence if present.
  const legacySt = searchParams.get('st');
  if (legacySt !== null && !searchParams.has('tt')) {
    if (legacySt === '1' || legacySt === 'true') {
      values.tenantType = TenantType.SINGLE;
    }
  }
  // Backward compatibility: legacy ?dst= mapped to the dedicated-tenant discount.
  const legacyDst = searchParams.get('dst');
  if (legacyDst !== null && !searchParams.has('dt')) {
    const num = parseFloat(legacyDst);
    if (!isNaN(num)) values.discountOnTenant = num;
  }
};

/**
 * Apply one URL parameter to the accumulating form values, by field kind.
 */
const applyParsedParam = (
  values: Partial<PricingCalculatorFormValues>,
  field: keyof PricingCalculatorFormValues,
  rawValue: string,
): void => {
  // Boolean fields
  if (BOOLEAN_PARAM_FIELDS.has(field)) {
    const boolValue = parseBool(rawValue);
    if (boolValue !== undefined) {
      (values as Record<string, boolean>)[field] = boolValue;
    }
    return;
  }

  // Number fields
  if (NUMBER_PARAM_FIELDS.has(field)) {
    const numValue = parseNum(rawValue);
    if (numValue !== undefined) {
      (values as Record<string, number>)[field] = numValue;
    }
    return;
  }

  switch (field) {
    // String fields
    case 'companyName':
      values.companyName = decodeURIComponent(rawValue);
      break;

    // Plan type enum
    case 'planType':
      if (Object.values(PlanType).includes(rawValue as PlanType)) {
        values.planType = rawValue as PlanType;
      }
      break;

    // Tenant type enum
    case 'tenantType':
      if (Object.values(TenantType).includes(rawValue as TenantType)) {
        values.tenantType = rawValue as TenantType;
      }
      break;

    // Array fields (comma-separated)
    case 'selectedIntegrations':
      values.selectedIntegrations = rawValue ? rawValue.split(',').filter(Boolean) : [];
      break;
  }
};

/**
 * Parse URL parameters into form values
 */
const parseUrlParams = (searchParams: URLSearchParams): Partial<PricingCalculatorFormValues> => {
  const values: Partial<PricingCalculatorFormValues> = {};

  applyLegacyParams(searchParams, values);

  // Parse each parameter
  for (const [param, field] of Object.entries(PARAM_TO_FIELD)) {
    const rawValue = searchParams.get(param);
    if (rawValue === null) continue;

    applyParsedParam(values, field, rawValue);
  }

  return values;
};

/**
 * Serializers below are split per field group purely to keep each function under the
 * repo complexity ceilings; each returns true when it owns (and has handled) the key.
 * The groups are disjoint, so the chained order in valuesToParams is not significant.
 */
type ParamSerializer = (
  params: Record<string, string>,
  param: string,
  key: keyof PricingCalculatorFormValues,
  value: unknown,
) => boolean;

// String and enum fields
const serializeTextParam: ParamSerializer = (params, param, key, value) => {
  switch (key) {
    // String fields
    case 'companyName':
      if (value) params[param] = encodeURIComponent(value as string);
      return true;

    // Plan and tenant type enums serialize the same way
    case 'planType':
    case 'tenantType':
      params[param] = value as string;
      return true;

    default:
      return false;
  }
};

// Boolean fields - use "1" for true (shorter than "true")
const serializeBooleanParam: ParamSerializer = (params, param, key, value) => {
  switch (key) {
    case 'isRenewal':
    case 'isItCustomer':
    case 'directDatabaseIntegration':
    case 'isolatedDatabase':
    case 'isolatedDatabaseMigration':
    case 'isolatedAi':
    case 'securityPackageSso':
    case 'govCloud':
    case 'aiForecasting':
    case 'nonPeopleResources':
    case 'profileBuilding':
    case 'skillsTagging':
    case 'solutionsSupport':
    case 'enableWhiteLabel':
    case 'enable4xDataTransfer':
    case 'integrationMigration':
      if (value) params[param] = '1';
      return true;

    default:
      return false;
  }
};

// Number fields
const serializeNumberParam: ParamSerializer = (params, param, key, value) => {
  switch (key) {
    case 'subscriptionDuration':
    case 'numberOfMembers':
    case 'pricePerMember':
    case 'discountOnMemberPrice':
    case 'numberOfInternalContractors':
    case 'numberOfGuests':
    case 'numberOfExternalContractors':
    case 'platformFee':
    case 'discountOnPlatformFee':
    case 'numberOfOnboardingGroups':
    case 'onboardingFee':
    case 'discountOnOnboardingFee':
    case 'additionalEnterpriseSupportHours':
    case 'discountOnDirectDatabase':
    case 'discountOnIsolatedDatabase':
    case 'discountOnTenant':
    case 'discountOnSecurityPackageSso':
    case 'govCloudFee':
    case 'discountOnGovCloudFee':
    case 'discountOnAiForecasting':
    case 'discountOnNonPeopleResources':
    case 'discountOnProfileBuilding':
    case 'discountOnSkillsTagging':
    case 'solutionsSupportHours':
    case 'bakIntegrationFetchTimes':
    case 'discountOnIntegrationSetup':
    case 'discountOnIntegrationRecurring':
      params[param] = String(value);
      return true;

    default:
      return false;
  }
};

// Array fields
const serializeArrayParam: ParamSerializer = (params, param, key, value) => {
  if (key !== 'selectedIntegrations') return false;

  if ((value as string[]).length > 0) {
    params[param] = (value as string[]).join(',');
  }
  return true;
};

const PARAM_SERIALIZERS: ParamSerializer[] = [
  serializeTextParam,
  serializeBooleanParam,
  serializeNumberParam,
  serializeArrayParam,
];

// Helper to check if value differs from default
const isDifferentFromDefault = (
  defaultValues: PricingCalculatorFormValues,
  key: keyof PricingCalculatorFormValues,
  value: unknown,
): boolean => {
  const defaultVal = defaultValues[key];
  if (Array.isArray(value) && Array.isArray(defaultVal)) {
    return JSON.stringify(value.sort()) !== JSON.stringify(defaultVal.sort());
  }
  return value !== defaultVal;
};

/**
 * Convert form values to URL parameters
 * Only includes non-default values to keep URLs shorter
 */
const valuesToParams = (
  values: PricingCalculatorFormValues,
  defaultValues: PricingCalculatorFormValues,
): Record<string, string> => {
  const params: Record<string, string> = {};

  // Add each field that differs from default
  for (const [field, param] of Object.entries(PARAM_KEYS)) {
    const key = field as keyof PricingCalculatorFormValues;
    const value = values[key];

    if (!isDifferentFromDefault(defaultValues, key, value)) continue;

    PARAM_SERIALIZERS.some((serialize) => serialize(params, param, key, value));
  }

  return params;
};

export interface UsePricingCalculatorUrlParamsProps {
  /** Current form values */
  values: PricingCalculatorFormValues;
  /** Default form values (used to determine which values to include in URL) */
  defaultValues: PricingCalculatorFormValues;
  /** Whether to sync form changes to URL (default: true) */
  syncToUrl?: boolean;
  /** Current active tab index (0 = Calculator, 1 = Pricing Quote) */
  activeTab?: number;
}

export interface UsePricingCalculatorUrlParamsResult {
  /** Initial values parsed from URL parameters */
  urlInitialValues: Partial<PricingCalculatorFormValues>;
  /** Initial tab parsed from URL parameters (defaults to 0) */
  urlInitialTab: number;
  /** Generate a shareable URL with current form values */
  getShareableUrl: () => string;
  /** Update URL with current form values */
  updateUrl: () => void;
}

/**
 * Hook to synchronize pricing calculator form values with URL parameters
 */
export const usePricingCalculatorUrlParams = ({
  values,
  defaultValues,
  syncToUrl = true,
  activeTab = 0,
}: UsePricingCalculatorUrlParamsProps): UsePricingCalculatorUrlParamsResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();

  // Parse URL parameters on mount
  const urlInitialValues = useMemo(() => {
    return parseUrlParams(searchParams);
    // Only parse on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parse initial tab from URL (defaults to 0)
  const urlInitialTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === '1' || tabParam === 'quote') return 1;
    return 0;
    // Only parse on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate shareable URL
  const getShareableUrl = useCallback(() => {
    const params = valuesToParams(values, defaultValues);
    // Add tab parameter if not on default tab (0)
    if (activeTab === 1) {
      params.tab = 'quote';
    }
    const queryString = new URLSearchParams(params).toString();
    const baseUrl = window.location.origin + PRICING_CALCULATOR_PATH;
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [values, defaultValues, activeTab]);

  // Update URL with current form values
  const updateUrl = useCallback(() => {
    const params = valuesToParams(values, defaultValues);
    // Add tab parameter if not on default tab (0)
    if (activeTab === 1) {
      params.tab = 'quote';
    }
    setSearchParams(params, { replace: true });
  }, [values, defaultValues, activeTab, setSearchParams]);

  // Debounced URL update when values change
  useEffect(() => {
    if (!syncToUrl) return;

    // Skip initial mount to avoid overwriting URL params
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce URL updates to avoid excessive history entries
    updateTimeoutRef.current = setTimeout(() => {
      updateUrl();
    }, 500);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [values, activeTab, syncToUrl, updateUrl]);

  return {
    urlInitialValues,
    urlInitialTab,
    getShareableUrl,
    updateUrl,
  };
};

export default usePricingCalculatorUrlParams;
